import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL,
  timeout: 10000, // 10 seconds timeout
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
});

// Request interceptor to attach token and handle request logging
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Log request details in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', {
        url: config.url,
        method: config.method,
        data: config.data,
        headers: config.headers
      });
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Setup response interceptor with injected refreshToken function
export const setupInterceptors = (refreshTokenFn, logoutFn) => {
  axiosInstance.interceptors.response.use(
    (response) => {
      // Log response details in development
      if (process.env.NODE_ENV === 'development') {
        console.log('API Response:', {
          url: response.config.url,
          status: response.status,
          data: response.data
        });
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Log the error for debugging
      console.error("API Error:", {
        status: error.response?.status,
        url: originalRequest.url,
        method: originalRequest.method,
        data: error.response?.data
      });

      // Skip token refresh for login requests
      if (originalRequest.url.includes("/auth/login/")) {
        return Promise.reject({
          message: "Invalid email or password. Please try again."
        });
      }

      // Handle 401 Unauthorized errors
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes("/auth/token/refresh/")
      ) {
        originalRequest._retry = true;

        try {
          const newAccess = await refreshTokenFn();
          originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;
          return axiosInstance(originalRequest);
        } catch (refreshErr) {
          console.error("Token refresh failed:", refreshErr);
          // Pass null to indicate normal logout
          logoutFn(null);
          return Promise.reject(refreshErr);
        }
      }

      // Handle inactive user response
      if (error.response?.data?.detail === "User account is inactive") {
        console.error("User account is inactive");
        logoutFn("Your account has been deactivated. Please contact support.");
        return Promise.reject(error);
      }

      // Handle network errors
      if (!error.response) {
        console.error("Network Error:", error.message);
        return Promise.reject({
          message: "Network error. Please check your connection."
        });
      }

      // Handle server errors
      if (error.response.status >= 500) {
        console.error("Server Error:", error.response.data);
        return Promise.reject({
          message: "Server error. Please try again later."
        });
      }

      // Handle client errors
      if (error.response.status >= 400) {
        console.error("Client Error:", error.response.data);
        return Promise.reject({
          message: error.response.data.detail || "An error occurred. Please try again."
        });
      }

      return Promise.reject(error);
    }
  );
};

// Add request retry logic for failed requests
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const { config } = error;
    if (!config || !config.retry) {
      return Promise.reject(error);
    }

    config.retryCount = config.retryCount || 0;

    if (config.retryCount >= config.retry) {
      return Promise.reject(error);
    }

    config.retryCount += 1;
    const backoff = new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, config.retryDelay || 1000);
    });

    await backoff;
    return axiosInstance(config);
  }
);

export default axiosInstance;
